import { GetStaticPropsResult } from 'next'
import React, { useEffect, useState, ReactElement } from 'react'
import { useForm } from 'react-hook-form'
import { PageMeta } from 'src/pages'
import { useMutation } from '@apollo/client'
import { ArrowForwardOutline } from 'react-ionicons'
import TagManager from 'react-gtm-module'
import { Layout } from '@/components/Layout'
import contactPageData from '@/content/contact.json'
import { HeroImage, HeroImageProps } from '@/components/patterns/HeroImage'
import {
  ContactSidebar,
  ContactSidebarProps,
} from '@/components/patterns/ContactSidebar'
import { ContactCTA, ContactCTAProps } from '@/components/patterns/ContactCTA'
import { Dialog } from '@/components/elements/Alert/dialog'
import { ContactInput } from '@/components/elements/ContactInput'
import { Checkbox } from '@/components/elements/Checkbox'
import { PrivacyPolicy } from '../activities/[id]/volunteer'
import SubmitButton from '@/components/elements/Button/submit'
import { Alert } from '@/components/elements/Alert'
import {
  SendContactMessageInput,
  SendContactMessageMutationMutationVariables,
} from '../../types/types'
import SEND_MESSAGE_MUTATION from '../../apollo/mutations/sendContactMessage.graphql'
import { IonIcon } from '@/src/icons'
import { CheckboxGroup } from '@/components/elements/Checkbox/group'
import { isValidEmail } from '@/utils/isValidEmail'

interface ContactPageProps {
  meta: PageMeta
  sidebar: ContactSidebarProps
  cta: ContactCTAProps
  heroImage: HeroImageProps
  successMessageDescription: string
  successMessageTitle: string
}

export async function getStaticProps(): Promise<
  GetStaticPropsResult<ContactPageProps>
> {
  return {
    props: {
      meta: {
        title: contactPageData.sidebar.title,
        description: contactPageData.sidebar.subtitle,
      },
      heroImage: {
        picture: contactPageData.picture,
        backgroundBlendColor: 'bg-navy',
      },
      sidebar: contactPageData.sidebar,
      cta: contactPageData.cta,
      successMessageDescription:
        contactPageData.formResult.successMessageDescription,
      successMessageTitle: contactPageData.formResult.successMessageTitle,
    },
  }
}

export default function ContactPage({
  meta,
  heroImage,
  sidebar,
  cta,
  successMessageTitle,
  successMessageDescription,
}: ContactPageProps): ReactElement {
  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm()

  const [errorMessages, setErrorMessages] = useState('')
  const [createMessage, messageState] =
    useMutation<SendContactMessageMutationMutationVariables>(
      SEND_MESSAGE_MUTATION,
      {
        onError: ({ networkError, graphQLErrors }) => {
          if (networkError) {
            setErrorMessages('there has been a network error')
          }
          if (graphQLErrors) {
            setErrorMessages(graphQLErrors[0].message)
          }
        },
      }
    )
  const { data: messageResponseData, loading, error } = messageState

  const submitMessage = ({
    name,
    surname,
    email,
    phone,
    message,
  }: SendContactMessageInput) => {
    createMessage({
      variables: {
        sendContactMessageData: { name, surname, email, phone, message },
      },
    }).then(() => {
      TagManager.dataLayer({
        dataLayer: {
          event: 'lead',
          form: 'form-lead-tandem-contacte',
        },
      })
    })
  }
  return (
    <Layout meta={meta}>
      <HeroImage {...heroImage} />
      <div>
        {!messageResponseData && (
          <div className="contact-page md:grid">
            <ContactSidebar {...sidebar} />
            <div className="pt-20 px-8 md:px-20">
              <ContactCTA {...cta} />
              <form
                onSubmit={handleSubmit(submitMessage)}
                className="mx-auto my-4 py-4 max-w-lg text-navy-900 font-body bg-white md:px-2 md:py-10"
              >
                <ContactInput
                  id="name"
                  label="Nom"
                  name="name"
                  validationRules={{
                    required: 'Empleneu aquest camp',
                  }}
                  register={register}
                  errors={errors}
                />
                <ContactInput
                  className="mt-4"
                  id="surname"
                  label="Cognoms"
                  name="surname"
                  validationRules={{
                    required: 'Empleneu aquest camp',
                  }}
                  register={register}
                  errors={errors}
                />
                <ContactInput
                  className="mt-4"
                  id="email-input"
                  label="Correu electrònic"
                  name="email"
                  validationRules={{
                    required: 'Empleneu aquest camp',
                    validate: isValidEmail,
                  }}
                  register={register}
                  errors={errors}
                />
                <ContactInput
                  className="mt-4"
                  id="phone-input"
                  label="Telèfon"
                  name="phone"
                  validationRules={{
                    required: 'Empleneu aquest camp',
                  }}
                  register={register}
                  errors={errors}
                />
                <ContactInput
                  isTextArea
                  rows="7"
                  className="mt-4"
                  id="message-input"
                  label="Missatge"
                  name="message"
                  validationRules={{
                    required: 'Empleneu aquest camp',
                  }}
                  register={register}
                  errors={errors}
                />
                <CheckboxGroup errors={errors} name="acceptPrivacy">
                  <Checkbox
                    name="acceptPrivacy"
                    register={register}
                    id="privacy-policy"
                    label="Accepto la política de privacitat"
                    description={<PrivacyPolicy />}
                    validationRules={{
                      required: 'Empleneu aquest camp',
                    }}
                  />
                </CheckboxGroup>

                <div className="mt-12">
                  <SubmitButton
                    className="px-6 w-full md:w-auto"
                    icon={<IonIcon icon={ArrowForwardOutline} />}
                    label="Enviar formulari"
                    isLoading={loading}
                  />
                </div>
                {error && (
                  <Alert
                    type="error"
                    className="mt-6"
                    title="Error en el formulari"
                    description={errorMessages}
                  />
                )}
              </form>
            </div>
          </div>
        )}
        {messageResponseData && (
          <Dialog
            title={successMessageTitle}
            description={successMessageDescription}
            type="success"
          />
        )}
      </div>
    </Layout>
  )
}
